import NameValue from "@/types/NameValue"
import React, { useState, useEffect, useRef } from "react"
import styles from "./FilterableSelect.module.scss"
import CrossIcon from "@/assets/svg/cross.svg"
import Loading from "@/components/common/Loading/Loading"

type SelectPickerItem = NameValue & {
  subName?: string
}

type FilterableSelectProps = {
  options: NameValue[] | SelectPickerItem[];
  name: string;
  value: string | null;
  onChange?: (value: string | null, name: string) => void;
  disabled?: boolean;
  placeholder?: string | null;
  enableFiltering?: boolean;
  loading?: boolean
};

const FilterableSelect: React.FC<FilterableSelectProps> = ({
  options,
  name,
  value,
  onChange,
  disabled = false,
  placeholder = "Select...",
  enableFiltering = true,
  loading = false,
}) => {
  const [search, setSearch] = useState<string>("")
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isTyping, setIsTyping] = useState<boolean>(false)
  const [highlightIndex, setHighlightIndex] = useState<number>(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const optionRefs = useRef<(HTMLLIElement | null)[]>([])

  const selectedOption = options.find((opt) => opt.value.toString() === value)
  const selectedLabel = selectedOption?.name || ""

  const filteredOptions = options.filter((opt) =>
    opt.name.toLowerCase().includes(search.toLowerCase())
  )

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setHighlightIndex(-1)
        setIsTyping(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    if (highlightIndex >= 0 && optionRefs.current[highlightIndex]) {
      optionRefs.current[highlightIndex]?.scrollIntoView({ block: "nearest" })
    }
  }, [highlightIndex])

  const handleSelect = (option: NameValue) => {
    onChange?.(option.value.toString(), name)
    setIsOpen(false)
    setHighlightIndex(-1)
    setIsTyping(false)
  }

  const handleClear = () => {
    if (!enableFiltering) return // Prevent clearing if filtering is disabled
    onChange?.(null, name)
    setSearch("")
    setIsTyping(false)
    setIsOpen(false)
    setHighlightIndex(-1)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || filteredOptions.length === 0) return

    if (e.key === "ArrowDown") {
      e.preventDefault()
      setHighlightIndex((prev) => Math.min(prev + 1, filteredOptions.length - 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setHighlightIndex((prev) => Math.max(prev - 1, 0))
    } else if (e.key === "Enter") {
      e.preventDefault()
      if (highlightIndex >= 0) {
        handleSelect(filteredOptions[highlightIndex])
      }
    } else if (e.key === "Escape") {
      e.preventDefault()
      setIsOpen(false)
      setHighlightIndex(-1)
    }
  }

  return (
    <div className={styles.component} ref={containerRef}>
      <div className={styles.inputWrapper}>
        {loading && (
          <div className={styles.loading}>
            <Loading
              small={true}
            />
          </div>
        )}
        <input
          type="text"
          disabled={disabled}
          placeholder={placeholder || ""}
          value={isTyping ? search : selectedLabel}
          onChange={(e) => {
            if (enableFiltering) {
              setSearch(e.target.value)
              setIsTyping(true)
              setIsOpen(true)
              setHighlightIndex(0)
            }
          }}
          onBlur={() => setIsTyping(false)}
          onClick={() => {
            if (!disabled) {
              setIsOpen(!isOpen)
              if (enableFiltering) setSearch("")
              const index = options.findIndex((opt) => opt.value.toString() === value)
              setHighlightIndex(index >= 0 ? index : 0)
            }
          }}
          onKeyDown={handleKeyDown}
          className={styles.input}
        />
        <div className={styles.icons}>
          {value && !disabled && enableFiltering && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={handleClear}
              aria-label="Clear selection"
            >
              <CrossIcon />
            </button>
          )}
          <span
            className={`${styles.arrow} ${isOpen ? styles.arrowOpen : ""}`}
          >
            ▼
          </span>
        </div>
      </div>
      {isOpen && !disabled && (
        <ul className={styles.dropdown}>
          {filteredOptions.map((option, index) => (
            <li
              key={option.value}
              ref={(el) => (optionRefs.current[index] = el)}
              className={`${styles.option} ${highlightIndex === index ? styles.highlight : ""}`}
              onMouseDown={() => handleSelect(option)}
            >
              <div>{option.name}</div>
              {(option as SelectPickerItem)?.subName && (
                <div className={styles.subname}>{(option as SelectPickerItem).subName}</div>
              )}
            </li>
          ))}
          {filteredOptions.length === 0 && (
            <li className={styles.notfound}>No results found</li>
          )}
        </ul>
      )}
    </div>
  )
}

export default FilterableSelect
